import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	SwitchField,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { Input } from "molecules/input/Input";
import { UserIdentitySummary } from "molecules/userIdentitySummary/UserIdentitySummary";
import { useTranslation } from "react-i18next";

import { getGravatarUrl } from "src/features/planning/utils/getGravatarUrl.js";

import {
	useGetAccessGroupsQuery,
	useInviteUserMutation,
	useUpdateUserMutation,
} from "../../common/redux/api/exopenApi";

import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { User } from "../../common/service/types/UserTypes.js";
import { useEffect, useId } from "react";
import { getGroupName } from "./getGroupName.js";

interface Inputs {
	email: string;
	groups: number[];
}

interface InviteUserModalProps {
	modalOpen: boolean;
	handleClose: () => void;
	companyId: string;
	user?: User | undefined;
}

export const InviteUserModal = ({
	modalOpen,
	handleClose,
	companyId,
	user,
}: InviteUserModalProps) => {
	const { t } = useTranslation();
	const emailId = useId();
	const { data: accessGroups } = useGetAccessGroupsQuery(companyId);
	const [inviteUser] = useInviteUserMutation();
	const [editUser] = useUpdateUserMutation();

	const {
		handleSubmit,
		register,
		formState: { isSubmitting, isDirty },
		control,
		reset,
	} = useForm<Inputs>({
		defaultValues: { email: user?.email ?? "", groups: [] },
	});

	const onClose = () => {
		handleClose();
		reset({ email: "", groups: [] });
	};

	useEffect(() => {
		if (user) {
			reset({
				email: user.email,
				groups: user.accessGroups.map((group) => group.groupId),
			});
		}
	}, [reset, user]);

	const onSubmit: SubmitHandler<Inputs> = async (data) => {
		const result = await (user
			? editUser({ companyId, accessGroups: data.groups, email: data.email })
			: inviteUser({
					email: data.email,
					accessGroups: data.groups,
					companyId,
				}));
		if ("error" in result) {
			showErrorNotification({
				message: user
					? t("The changes couldn't be saved")
					: t("Error occurred when user was invited"),
			});
		} else {
			showSuccessNotification({
				message: user
					? t("The changes are saved")
					: t("An invite was sent to {{email}}", { email: data.email }),
			});
			onClose();
		}
	};

	return (
		<Dialog open={modalOpen} onClose={onClose}>
			<DialogContent>
				<DialogTitle>{user ? t("Edit user") : t("Invite users")}</DialogTitle>
				<DialogBody>
					<form
						id="user-form"
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						{user ? (
							<>
								<UserIdentitySummary
									avatarSrc={getGravatarUrl(user.email)}
									name={user.name}
									email={user.email}
								/>
								<input type="hidden" {...register("email")} />
							</>
						) : (
							<div>
								<Label htmlFor={emailId}>{t("Email")}</Label>
								<Input
									{...register("email")}
									required
									id={emailId}
									type="email"
								/>
							</div>
						)}
						<div>
							<Label>{t("Groups")}</Label>
							<div className="space-y-2">
								<Controller
									control={control}
									name="groups"
									render={({ field: { onChange, value, ...field } }) => {
										return (
											<>
												{(accessGroups ?? []).map((group) => (
													<div key={group.groupId}>
														<SwitchField
															{...field}
															checked={value.includes(group.groupId)}
															onChange={(event) => {
																if (event.target.checked) {
																	onChange([...value, group.groupId]);
																} else {
																	onChange(
																		value.filter((martId) => {
																			return martId !== group.groupId;
																		}),
																	);
																}
															}}
															label={getGroupName({ groupName: group.name, t })}
														/>
													</div>
												))}
											</>
										);
									}}
								/>
							</div>
						</div>
					</form>
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						form="user-form"
						type="submit"
						isLoading={isSubmitting}
						disabled={isSubmitting || !isDirty}
					>
						{user ? t("Save changes") : t("Send invite")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};
